import React, { useState, useEffect } from "react";
import { useUser, useClerk } from "@clerk/clerk-react";

const PROXY_URL = process.env.REACT_APP_PROXY_URL || "http://localhost:3001";

// ── Feature pill ───────────────────────────────────────────────────────────
function Pill({ icon, text }) {
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 7,
      background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)",
      borderRadius: 100, padding: "8px 16px", fontSize: 13, color: "#a8a29e",
    }}>
      <span>{icon}</span><span>{text}</span>
    </div>
  );
}

// ── Main Landing Page ──────────────────────────────────────────────────────
export default function LandingPage() {
  const { user, isSignedIn } = useUser();
  const { signOut } = useClerk();
  const [isPro, setIsPro] = useState(false);

  useEffect(() => {
    if (!isSignedIn || !user) return;
    fetch(`${PROXY_URL}/api/stripe/subscription/${user.id}`)
      .then(r => r.json())
      .then(d => { if (d.subscribed) setIsPro(true); })
      .catch(() => {});
  }, [isSignedIn, user]);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Sans:wght@400;500;600&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: #0c0a09; }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; } 50% { opacity: 0.4; }
        }
        @keyframes shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .nav-cta:hover { opacity: 0.85; }
        .feature-card:hover { border-color: rgba(217,119,6,0.3) !important; transform: translateY(-2px); }
        input:focus { outline: none; border-color: rgba(217,119,6,0.6) !important; }
        select:focus { outline: none; }
        @media (max-width: 640px) {
          /* Nav */
          .nav-toolkit-label { display: none !important; }
          .nav-name-greeting { display: none !important; }
          .nav-pro-badge { display: none !important; }
          .nav-signin-link { display: none !important; }
          .nav-actions { gap: 8px !important; }
          .nav-cta { font-size: 12px !important; padding: 7px 13px !important; }
          /* Hero */
          .hero-title { font-size: 36px !important; }
          .hero-sub { font-size: 16px !important; }
          /* Demo */
          /* Proof */
          /* Features */
          .features-grid { grid-template-columns: 1fr !important; }
          /* Spotlights */
          .spotlight-grid { grid-template-columns: 1fr !important; padding: 28px 22px !important; }
          /* Pricing */
          .pricing-grid { grid-template-columns: 1fr !important; }
          /* Pills */
          .pills-row { gap: 8px !important; }
          /* Bottom CTA */
          .bottom-cta-title { font-size: 28px !important; }
        }
      `}</style>

      <div style={{ background: "#0c0a09", minHeight: "100vh", fontFamily: "'DM Sans', sans-serif", color: "#fafaf9" }}>

        {/* ── NAV ── */}
        <nav style={{
          position: "sticky", top: 0, zIndex: 100,
          background: "rgba(12,10,9,0.9)", backdropFilter: "blur(12px)",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          padding: "0 24px", height: 60,
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 32, height: 32, background: "#d97706", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 800, color: "#0c0a09", fontFamily: "'Syne', sans-serif" }}>✦</div>
            <span style={{ fontFamily: "'Syne', sans-serif", fontSize: 20, fontWeight: 800, color: "#fafaf9" }}>Limji</span>
            <span className="nav-toolkit-label" style={{ fontSize: 10, fontWeight: 700, color: "#57534e", letterSpacing: "0.1em", marginLeft: 2 }}>AI TOOLKIT</span>
          </div>
          <div className="nav-actions" style={{ display: "flex", gap: 10, alignItems: "center" }}>
            {isSignedIn ? (
              <>
                {isPro && (
                  <span className="nav-pro-badge" style={{ fontSize: 11, fontWeight: 700, color: "#d97706", background: "rgba(217,119,6,0.1)", border: "1px solid rgba(217,119,6,0.3)", borderRadius: 100, padding: "3px 10px", letterSpacing: "0.06em" }}>PRO</span>
                )}
                <span className="nav-name-greeting" style={{ fontSize: 13, color: "#78716c" }}>
                  Hi, {user?.firstName || "there"} 👋
                </span>
                <a href="/app" className="nav-cta" style={{
                  fontSize: 13, fontWeight: 700, color: "#0c0a09",
                  background: "#d97706", borderRadius: 8, padding: "8px 18px",
                  textDecoration: "none", transition: "opacity 0.2s",
                }}>Open Limji →</a>
                {/* Avatar → /account */}
                <a href="/account" title="My account" style={{ display: "flex", alignItems: "center", textDecoration: "none", flexShrink: 0 }}>
                  {user?.imageUrl ? (
                    <img src={user.imageUrl} alt="" style={{ width: 32, height: 32, borderRadius: "50%", border: "2px solid rgba(217,119,6,0.4)" }} />
                  ) : (
                    <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#d97706", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800, color: "#0c0a09" }}>
                      {user?.firstName?.[0] || "?"}
                    </div>
                  )}
                </a>
                {/* Sign out */}
                <button
                  onClick={() => signOut({ redirectUrl: "/" })}
                  title="Sign out"
                  style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, padding: "6px 9px", cursor: "pointer", fontSize: 14, color: "#57534e", display: "flex", alignItems: "center", lineHeight: 1, flexShrink: 0 }}
                >
                  ↪
                </button>
              </>
            ) : (
              <>
                <a href="/sign-in" className="nav-signin-link" style={{ fontSize: 13, fontWeight: 600, color: "#a8a29e", textDecoration: "none", padding: "6px 14px" }}>Sign in</a>
                <a href="/app" className="nav-cta" style={{
                  fontSize: 13, fontWeight: 700, color: "#0c0a09",
                  background: "#d97706", borderRadius: 8, padding: "8px 18px",
                  textDecoration: "none", transition: "opacity 0.2s", whiteSpace: "nowrap",
                }}>Try free →</a>
              </>
            )}
          </div>
        </nav>

        {/* ── HERO ── */}
        <div style={{ maxWidth: 760, margin: "0 auto", padding: "80px 24px 60px", textAlign: "center" }}>

          {/* Badge */}
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            background: "rgba(217,119,6,0.1)", border: "1px solid rgba(217,119,6,0.25)",
            borderRadius: 100, padding: "6px 16px", fontSize: 12, fontWeight: 700,
            color: "#fbbf24", marginBottom: 32, letterSpacing: "0.05em",
          }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#d97706", animation: "pulse 2s infinite", display: "inline-block" }} />
            BUILT FOR SMALL BUSINESSES WORLDWIDE
          </div>

          {/* Title */}
          <h1 className="hero-title" style={{
            fontFamily: "'Syne', sans-serif", fontSize: 56, fontWeight: 800,
            lineHeight: 1.1, letterSpacing: "-0.02em", marginBottom: 20,
            color: "#fafaf9",
          }}>
            Business copy that<br />
            <span style={{
              background: "linear-gradient(90deg, #d97706, #fbbf24, #d97706)",
              backgroundSize: "200% auto",
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
              animation: "shimmer 3s linear infinite",
            }}>
              sounds like you
            </span>
          </h1>

          {/* Subtitle */}
          <p className="hero-sub" style={{ fontSize: 18, color: "#78716c", lineHeight: 1.7, marginBottom: 40, maxWidth: 520, margin: "0 auto 40px" }}>
            AI-powered ads, emails, and messages — written for your business, ready to send in seconds.
          </p>

          {/* Pills */}
          <div className="pills-row" style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 10, marginBottom: 56 }}>
            <Pill icon="📢" text="Smart Ads" />
            <Pill icon="✉️" text="Smart Email" />
            <Pill icon="💬" text="WhatsApp Ready" />
            <Pill icon="🌍" text="Any Business, Anywhere" />
          </div>

          {/* ── CTA BUTTONS ── */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14, animation: "fadeUp 0.6s ease" }}>
            {isSignedIn ? (
              <a href="/app" style={{
                display: "inline-flex", alignItems: "center", gap: 10,
                padding: "17px 40px", background: "#d97706", borderRadius: 14,
                fontSize: 16, fontWeight: 800, color: "#0c0a09",
                textDecoration: "none", fontFamily: "'Syne', sans-serif",
                boxShadow: "0 0 40px rgba(217,119,6,0.3)", transition: "opacity 0.2s",
              }}>
                ✦ Open Limji {isPro ? "(Pro)" : "→"}
              </a>
            ) : (
              <a href="/app" style={{
                display: "inline-flex", alignItems: "center", gap: 10,
                padding: "17px 40px", background: "#d97706", borderRadius: 14,
                fontSize: 16, fontWeight: 800, color: "#0c0a09",
                textDecoration: "none", fontFamily: "'Syne', sans-serif",
                boxShadow: "0 0 40px rgba(217,119,6,0.3)", transition: "opacity 0.2s",
              }}>
                ✦ Try free — no signup needed →
              </a>
            )}
            <div style={{ fontSize: 13, color: "#44403c" }}>
              No credit card &nbsp;·&nbsp; 10 free generations &nbsp;·&nbsp; Takes 10 seconds
            </div>
          </div>
        </div>

        {/* ── FEATURES ── */}
        <div style={{ maxWidth: 1000, margin: "0 auto", padding: "0 24px 80px" }}>
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <h2 style={{ fontFamily: "'Syne', sans-serif", fontSize: 36, fontWeight: 800, color: "#fafaf9", marginBottom: 12 }}>
              Everything your business needs to communicate
            </h2>
            <p style={{ fontSize: 16, color: "#78716c" }}>From the first ad to the final follow-up</p>

          </div>

          <div className="features-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
            {[
              { icon: "📢", title: "Smart Ads", desc: "Ads for Instagram, Facebook, WhatsApp, and more. 9 categories, any market." },
              { icon: "✉️", title: "Smart Email", desc: "15+ email types — invoices, proposals, follow-ups, cold outreach. Two versions every time." },
              { icon: "💬", title: "WhatsApp Ready", desc: "Every email comes with a WhatsApp-ready version. Copy and send in seconds." },
              { icon: "📦", title: "Smart Orders", desc: "Track customer orders, send status updates via WhatsApp, and manage your pipeline — no spreadsheet needed." },
              { icon: "📄", title: "PDF Export", desc: "Export any result as a branded PDF. Professional and ready to share or print." },
              { icon: "⚡", title: "10 seconds", desc: "Type your details, hit generate. Real copy in under 10 seconds. No prompting skills needed." },
            ].map((f, i) => (
              <div key={i} className="feature-card" style={{
                background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)",
                borderRadius: 16, padding: "24px 22px", transition: "all 0.2s",
              }}>
                <div style={{ fontSize: 28, marginBottom: 12 }}>{f.icon}</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#fafaf9", marginBottom: 8, fontFamily: "'Syne', sans-serif" }}>{f.title}</div>
                <div style={{ fontSize: 13, color: "#78716c", lineHeight: 1.7 }}>{f.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── SMART ADS SPOTLIGHT ── */}
        <div style={{ maxWidth: 860, margin: "0 auto", padding: "0 24px 32px" }}>
          <div style={{
            background: "rgba(255,255,255,0.02)",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 24, padding: "48px 40px",
            display: "grid", gridTemplateColumns: "1fr 1fr", gap: 40, alignItems: "center",
          }} className="spotlight-grid">
            {/* Left — text */}
            <div>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 100, padding: "4px 14px", fontSize: 11, fontWeight: 700, color: "#a8a29e", letterSpacing: "0.08em", marginBottom: 20 }}>
                📢 SMART ADS
              </div>
              <h3 style={{ fontFamily: "'Syne', sans-serif", fontSize: 28, fontWeight: 800, color: "#fafaf9", lineHeight: 1.2, marginBottom: 14, letterSpacing: "-0.01em" }}>
                Ads that sound like<br />a real business.
              </h3>
              <p style={{ fontSize: 15, color: "#78716c", lineHeight: 1.8, marginBottom: 20 }}>
                Describe what you sell, pick a category, and get a punchy ad with a headline, body, CTA, and a tactical tip — in under 10 seconds.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {[
                  "9 ad categories — Instagram, WhatsApp, Dubizzle & more",
                  "Version A (direct) + Version B (story-led) with Pro",
                  "Built-in tip for each ad so you know how to run it",
                  "Copy or export as PDF in one click",
                ].map((item, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 14, color: "#a8a29e" }}>
                    <span style={{ color: "#d97706", fontWeight: 800, flexShrink: 0 }}>✦</span>
                    {item}
                  </div>
                ))}
              </div>
              <a href="/app?tab=ads" style={{
                display: "inline-block", marginTop: 24,
                padding: "11px 24px", background: "rgba(255,255,255,0.06)", color: "#fafaf9",
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: 10, fontSize: 14, fontWeight: 700,
                textDecoration: "none", fontFamily: "'Syne', sans-serif",
              }}>
                "Write an ad →"
              </a>
            </div>
            {/* Right — mock ad result */}
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(217,119,6,0.25)", borderRadius: 16, padding: "22px 20px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                  <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#d97706", animation: "pulse 2s infinite" }} />
                  <span style={{ fontSize: 11, fontWeight: 700, color: "#d97706", letterSpacing: "0.1em", textTransform: "uppercase" }}>Generated for Ahmed's Car Wash</span>
                </div>
                <div style={{ fontSize: 20, fontWeight: 800, color: "#fafaf9", lineHeight: 1.3, marginBottom: 10, fontFamily: "'Syne', sans-serif" }}>
                  Your car deserves better than a quick rinse.
                </div>
                <div style={{ fontSize: 13, color: "#a8a29e", lineHeight: 1.8, marginBottom: 14 }}>
                  We hand-wash every vehicle like it's our own. Interior vacuum, tyre shine, streak-free glass — done in 45 minutes while you wait.
                </div>
                <div style={{ display: "inline-block", background: "rgba(217,119,6,0.15)", border: "1px solid rgba(217,119,6,0.4)", borderRadius: 8, padding: "5px 12px", fontSize: 12, fontWeight: 700, color: "#fbbf24" }}>
                  Book your slot today →
                </div>
                <div style={{ marginTop: 14, padding: "10px 14px", background: "rgba(217,119,6,0.07)", borderRadius: 8, borderLeft: "3px solid #d97706" }}>
                  <span style={{ fontSize: 11, color: "#fbbf24" }}>💡 Add a before/after photo — car wash ads with visuals get 3× more saves on Instagram.</span>
                </div>
              </div>
              {/* Version B teaser */}
              <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: "14px 16px", position: "relative", overflow: "hidden" }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#57534e", letterSpacing: "0.08em", marginBottom: 6 }}>VERSION B — PRO</div>
                <div style={{ fontSize: 13, color: "#44403c", lineHeight: 1.6, filter: "blur(3px)", userSelect: "none" }}>
                  Tired of a dirty car ruining your day? Ahmed's gives your ride the full treatment — inside and out — in under an hour.
                </div>
                <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(12,10,9,0.6)", borderRadius: 12 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: "#57534e" }}>🔒 Pro unlocks Version B</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── SMART EMAIL SPOTLIGHT ── */}
        <div style={{ maxWidth: 860, margin: "0 auto", padding: "0 24px 32px" }}>
          <div style={{
            background: "rgba(255,255,255,0.02)",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 24, padding: "48px 40px",
            display: "grid", gridTemplateColumns: "1fr 1fr", gap: 40, alignItems: "center",
          }} className="spotlight-grid">
            {/* Left — text */}
            <div>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 100, padding: "4px 14px", fontSize: 11, fontWeight: 700, color: "#a8a29e", letterSpacing: "0.08em", marginBottom: 20 }}>
                ✉️ SMART EMAIL
              </div>
              <h3 style={{ fontFamily: "'Syne', sans-serif", fontSize: 28, fontWeight: 800, color: "#fafaf9", lineHeight: 1.2, marginBottom: 14, letterSpacing: "-0.01em" }}>
                Every email your<br />business will ever need.
              </h3>
              <p style={{ fontSize: 15, color: "#78716c", lineHeight: 1.8, marginBottom: 20 }}>
                From cold outreach to payment reminders to follow-ups. 15+ email types, two versions each, plus a WhatsApp-ready version so you can send it anywhere.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {[
                  "15+ types: cold outreach, invoice, proposal, follow-up & more",
                  "WhatsApp version included — shorter, warmer, ready to paste",
                  "48h follow-up email generated automatically with Pro",
                  "Export to PDF or copy in one click",
                ].map((item, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 14, color: "#a8a29e" }}>
                    <span style={{ color: "#d97706", fontWeight: 800, flexShrink: 0 }}>✦</span>
                    {item}
                  </div>
                ))}
              </div>
              <a href="/app?tab=email" style={{
                display: "inline-block", marginTop: 24,
                padding: "11px 24px", background: "rgba(255,255,255,0.06)", color: "#fafaf9",
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: 10, fontSize: 14, fontWeight: 700,
                textDecoration: "none", fontFamily: "'Syne', sans-serif",
              }}>
                "Write an email →"
              </a>
            </div>
            {/* Right — mock email result */}
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 16, padding: "22px 20px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                  <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#d97706", animation: "pulse 2s infinite" }} />
                  <span style={{ fontSize: 11, fontWeight: 700, color: "#d97706", letterSpacing: "0.1em", textTransform: "uppercase" }}>Cold Outreach — Nour Cleaning</span>
                </div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#57534e", marginBottom: 4, letterSpacing: "0.06em" }}>SUBJECT</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#fafaf9", marginBottom: 14 }}>
                  Quick question about your office cleaning
                </div>
                <div style={{ fontSize: 13, color: "#a8a29e", lineHeight: 1.9 }}>
                  Hi [Name],<br /><br />
                  I noticed your office on LinkedIn — we help businesses like yours keep their space clean without the hassle of managing a cleaning team in-house.<br /><br />
                  We cover everything from daily maintenance to deep cleans, and we're fully insured. Would a 15-minute call this week make sense?
                </div>
                <div style={{ marginTop: 14, padding: "10px 14px", background: "rgba(217,119,6,0.07)", borderRadius: 8, borderLeft: "3px solid #d97706" }}>
                  <span style={{ fontSize: 11, color: "#fbbf24" }}>💡 Send on Tuesday or Wednesday morning — open rates are 35% higher mid-week.</span>
                </div>
              </div>
              {/* WhatsApp version teaser */}
              <div style={{ background: "rgba(37,211,102,0.05)", border: "1px solid rgba(37,211,102,0.15)", borderRadius: 12, padding: "14px 16px", display: "flex", alignItems: "flex-start", gap: 12 }}>
                <span style={{ fontSize: 18, flexShrink: 0 }}>💬</span>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#25d366", marginBottom: 4, letterSpacing: "0.06em" }}>WHATSAPP VERSION</div>
                  <div style={{ fontSize: 13, color: "#78716c", lineHeight: 1.7 }}>
                    "Hi [Name], I run Nour Cleaning — we handle office cleaning so you don't have to manage a team. Quick call this week?"
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── SMART ORDERS SPOTLIGHT ── */}
        <div style={{ maxWidth: 860, margin: "0 auto", padding: "0 24px 80px" }}>
          <div style={{
            background: "rgba(255,255,255,0.02)",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 24, padding: "48px 40px",
            display: "grid", gridTemplateColumns: "1fr 1fr", gap: 40, alignItems: "center",
          }} className="spotlight-grid">
            {/* Left — text */}
            <div>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 100, padding: "4px 14px", fontSize: 11, fontWeight: 700, color: "#a8a29e", letterSpacing: "0.08em", marginBottom: 20 }}>
                📦 SMART ORDERS
              </div>
              <h3 style={{ fontFamily: "'Syne', sans-serif", fontSize: 28, fontWeight: 800, color: "#fafaf9", lineHeight: 1.2, marginBottom: 14, letterSpacing: "-0.01em" }}>
                Run your orders.<br />Not a spreadsheet.
              </h3>
              <p style={{ fontSize: 15, color: "#78716c", lineHeight: 1.8, marginBottom: 20 }}>
                Add orders, track status, and send WhatsApp updates to customers — all in one place. Built for small businesses that run on messages, not software.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {[
                  "Add orders with customer name, phone & details",
                  "Track status: New → In Progress → Ready → Delivered",
                  "One-tap WhatsApp update messages",
                  "Free: 10 active orders · Pro: unlimited",
                ].map((item, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 14, color: "#a8a29e" }}>
                    <span style={{ color: "#d97706", fontWeight: 800, flexShrink: 0 }}>✦</span>
                    {item}
                  </div>
                ))}
              </div>
              <a href="/app?tab=orders" style={{
                display: "inline-block", marginTop: 24,
                padding: "11px 24px", background: "rgba(255,255,255,0.06)", color: "#fafaf9",
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: 10, fontSize: 14, fontWeight: 700,
                textDecoration: "none", fontFamily: "'Syne', sans-serif",
              }}>
                "Try Smart Orders →"
              </a>
            </div>
            {/* Right — mock orders UI */}
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 16, padding: "22px 20px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                  <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#d97706", animation: "pulse 2s infinite" }} />
                  <span style={{ fontSize: 11, fontWeight: 700, color: "#d97706", letterSpacing: "0.1em", textTransform: "uppercase" }}>Active Orders — Nour Tailoring</span>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {[
                    { name: "Sara Al Mansoori", item: "Abaya alteration × 2", status: "Ready", color: "#22c55e" },
                    { name: "Ahmed Khalil", item: "Suit fitting + hem", status: "In Progress", color: "#f59e0b" },
                    { name: "Maria Santos", item: "3× dress alterations", status: "New", color: "#78716c" },
                  ].map((order, i) => (
                    <div key={i} style={{
                      background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)",
                      borderRadius: 10, padding: "12px 14px",
                      display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
                      animation: `fadeUp 0.4s ease ${i * 0.08}s both`,
                    }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: "#fafaf9", marginBottom: 2 }}>{order.name}</div>
                        <div style={{ fontSize: 12, color: "#57534e" }}>{order.item}</div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: order.color, background: `${order.color}18`, border: `1px solid ${order.color}40`, borderRadius: 100, padding: "3px 10px", whiteSpace: "nowrap" }}>
                          {order.status}
                        </div>
                        <span style={{ fontSize: 15 }}>💬</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 14, padding: "10px 14px", background: "rgba(37,211,102,0.07)", borderRadius: 8, borderLeft: "3px solid rgba(37,211,102,0.4)" }}>
                  <span style={{ fontSize: 11, color: "#25d366" }}>💬 Sara's order is ready — tap to send her a WhatsApp update in one click.</span>
                </div>
              </div>
              <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: "14px 16px", display: "flex", alignItems: "flex-start", gap: 12 }}>
                <span style={{ fontSize: 18, flexShrink: 0 }}>📦</span>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#57534e", marginBottom: 4, letterSpacing: "0.06em" }}>NO SPREADSHEET NEEDED</div>
                  <div style={{ fontSize: 13, color: "#78716c", lineHeight: 1.7 }}>
                    Free accounts get 10 active orders. Pro gets unlimited orders + priority WhatsApp templates.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── HOW IT WORKS ── */}
        <div style={{ maxWidth: 700, margin: "0 auto", padding: "0 24px 80px", textAlign: "center" }}>
          <h2 style={{ fontFamily: "'Syne', sans-serif", fontSize: 36, fontWeight: 800, color: "#fafaf9", marginBottom: 48 }}>
            Simple as WhatsApp
          </h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
            {[
              { num: "01", title: "Describe your business", desc: "Tell Limji what you sell, who you're writing to, and what you need." },
              { num: "02", title: "Get two versions instantly", desc: "Version A is direct and clear. Version B is warmer and relationship-first. Pick the one that fits." },
              { num: "03", title: "Copy, send, done", desc: "Copy the text, send the WhatsApp message, or export as PDF. Takes less than a minute." },
            ].map((step, i) => (
              <div key={i} style={{ display: "flex", gap: 20, textAlign: "left", paddingBottom: i < 2 ? 32 : 0, borderLeft: i < 2 ? "1px solid rgba(255,255,255,0.06)" : "none", marginLeft: 20, paddingLeft: 32, position: "relative" }}>
                <div style={{ position: "absolute", left: -20, top: 0, width: 40, height: 40, borderRadius: "50%", background: "rgba(217,119,6,0.15)", border: "1px solid rgba(217,119,6,0.3)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, color: "#fbbf24", fontFamily: "'Syne', sans-serif", flexShrink: 0 }}>{step.num}</div>
                <div style={{ paddingTop: 8 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#fafaf9", marginBottom: 6, fontFamily: "'Syne', sans-serif" }}>{step.title}</div>
                  <div style={{ fontSize: 14, color: "#78716c", lineHeight: 1.7 }}>{step.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── PRICING ── */}
        <div style={{ maxWidth: 800, margin: "0 auto", padding: "0 24px 80px" }}>
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <h2 style={{ fontFamily: "'Syne', sans-serif", fontSize: 36, fontWeight: 800, color: "#fafaf9", marginBottom: 12 }}>Simple pricing</h2>
            <p style={{ fontSize: 15, color: "#78716c" }}>Start free. Upgrade when you're ready.</p>
          </div>
          <div className="pricing-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            {/* Free */}
            <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 20, padding: "32px 28px" }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#57534e", letterSpacing: "0.08em", marginBottom: 16 }}>FREE</div>
              <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 40, fontWeight: 800, color: "#fafaf9", marginBottom: 4 }}>$0</div>
              <div style={{ fontSize: 13, color: "#78716c", marginBottom: 24 }}>3 generations/day</div>
              {["Smart Ads", "Smart Email", "Version A", "History (this device)", "10 free tries to start"].map((f, i) => (
                <div key={i} style={{ fontSize: 13, color: "#a8a29e", marginBottom: 10, display: "flex", gap: 8 }}>
                  <span style={{ color: "#57534e" }}>✓</span> {f}
                </div>
              ))}
              <a href="/app" style={{ display: "block", marginTop: 24, padding: "12px 0", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, fontSize: 14, fontWeight: 700, color: "#a8a29e", textDecoration: "none", textAlign: "center", fontFamily: "inherit" }}>
                "Start free →"
              </a>
            </div>
            {/* Pro */}
            <div style={{ background: "rgba(217,119,6,0.08)", border: "1px solid rgba(217,119,6,0.3)", borderRadius: 20, padding: "32px 28px", position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", top: 16, right: 16, background: "#d97706", borderRadius: 100, padding: "3px 12px", fontSize: 11, fontWeight: 800, color: "#0c0a09" }}>POPULAR</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#d97706", letterSpacing: "0.08em", marginBottom: 16 }}>PRO</div>
              <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 40, fontWeight: 800, color: "#fafaf9", marginBottom: 4 }}>$19<span style={{ fontSize: 16, color: "#78716c" }}>/mo</span></div>
              <div style={{ fontSize: 13, color: "#78716c", marginBottom: 24 }}>150 generations/day</div>
              {["Everything in Free", "Version B (alternative angle)", "WhatsApp message", "48h follow-up email", "PDF export", "History across devices", "Priority support"].map((f, i) => (
                <div key={i} style={{ fontSize: 13, color: "#a8a29e", marginBottom: 10, display: "flex", gap: 8 }}>
                  <span style={{ color: "#d97706" }}>✦</span> {f}
                </div>
              ))}
              <a href={isPro ? "/app" : isSignedIn ? "/account" : "/app"} style={{ display: "block", marginTop: 24, padding: "12px 0", background: "#d97706", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 800, color: "#0c0a09", textDecoration: "none", textAlign: "center", fontFamily: "inherit" }}>
                {isPro ? "Open Limji ✦" : isSignedIn ? "Upgrade to Pro ✦" : "Try free → upgrade anytime"}
              </a>
            </div>
          </div>
        </div>

        {/* ── BOTTOM CTA ── */}
        <div style={{ textAlign: "center", padding: "80px 24px", borderTop: "1px solid rgba(255,255,255,0.06)", background: "rgba(217,119,6,0.03)" }}>
          <h2 className="bottom-cta-title" style={{ fontFamily: "'Syne', sans-serif", fontSize: 40, fontWeight: 800, color: "#fafaf9", marginBottom: 16, lineHeight: 1.2 }}>
            Your next message<br />is 10 seconds away
          </h2>
          <p style={{ fontSize: 16, color: "#78716c", marginBottom: 32 }}>No credit card. No prompting skills. Just type and generate.</p>
          <a href="/app" style={{
            display: "inline-block", padding: "16px 40px",
            background: "#d97706", color: "#0c0a09",
            borderRadius: 12, fontSize: 16, fontWeight: 800,
            textDecoration: "none", fontFamily: "'Syne', sans-serif",
            boxShadow: "0 8px 32px rgba(217,119,6,0.3)",
            transition: "all 0.2s", animation: "float 3s ease-in-out infinite",
          }}>
            {isSignedIn ? "✦ Open Limji →" : "✦ Try Limji free →"}
          </a>
          <div style={{ marginTop: 16, fontSize: 13, color: "#44403c" }}>
            Used by businesses in UAE, UK, Canada, Singapore and more
          </div>
        </div>

        {/* ── FOOTER ── */}
        <footer style={{ padding: "24px", textAlign: "center", borderTop: "1px solid rgba(255,255,255,0.04)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 12 }}>
            <div style={{ width: 24, height: 24, background: "#d97706", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: "#0c0a09" }}>✦</div>
            <span style={{ fontFamily: "'Syne', sans-serif", fontSize: 16, fontWeight: 800, color: "#57534e" }}>Limji</span>
          </div>
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 20, marginBottom: 12, flexWrap: "wrap" }}>
            <a href="/privacy" style={{ fontSize: 12, color: "#44403c", textDecoration: "none" }}>Privacy Policy</a>
            <span style={{ fontSize: 12, color: "#292524" }}>·</span>
            <a href="/terms" style={{ fontSize: 12, color: "#44403c", textDecoration: "none" }}>Terms of Service</a>
            <span style={{ fontSize: 12, color: "#292524" }}>·</span>
            <a href="mailto:support@limji.app" style={{ fontSize: 12, color: "#44403c", textDecoration: "none" }}>support@limji.app</a>
          </div>
          <div style={{ fontSize: 12, color: "#292524" }}>
            © 2026 Limji · AI Tools for Small Businesses
          </div>
        </footer>

      </div>
    </>
  );
}
